import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import useAuth from '../hooks/useAuth';
import { permissionsApi } from '../api/permissions';
import CATALOG from './capability-catalog.json';

const REFRESH_MS = 120_000;

export const CapabilitiesContext = createContext({
  can: () => false,
  capabilities: null,
  matrixEnabled: false,
  loading: true,
  refresh: () => {},
});

export function CapabilitiesProvider({ children }) {
  const { role, hasAnyRole } = useAuth();
  const [capabilities, setCapabilities] = useState(null);
  const [matrixEnabled, setMatrixEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);

  const refresh = useCallback(() => {
    if (!role) {
      setLoading(false);
      return;
    }
    permissionsApi
      .getMine()
      .then((res) => {
        if (!alive.current) return;
        setCapabilities(res.capabilities || []);
        setMatrixEnabled(!!res.matrixEnabled);
      })
      // Falha de rede mantém o fallback local — nunca esvazia o menu.
      .catch(() => {})
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  }, [role]);

  useEffect(() => {
    alive.current = true;
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => {
      alive.current = false;
      clearInterval(id);
    };
  }, [refresh]);

  // Antes da resposta (ou se ela falhar), decide pelo default do catálogo:
  // idêntico ao comportamento hardcoded de hoje, sem sidebar vazia piscando.
  const can = useCallback(
    (capability) => {
      const def = CATALOG[capability];
      if (!def) return false;
      if (capabilities) return capabilities.includes(capability);
      return hasAnyRole(def.default);
    },
    [capabilities, hasAnyRole],
  );

  return (
    <CapabilitiesContext.Provider
      value={{ can, capabilities, matrixEnabled, loading, refresh }}
    >
      {children}
    </CapabilitiesContext.Provider>
  );
}

export const useCapabilities = () => useContext(CapabilitiesContext);
